/**
 * Personalization — wallpaper + avatar, chosen in My profile, applied across
 * the shell (wallpaper on desktop AND lock screen, avatar in the menu bar).
 *
 * Device-level on purpose (localStorage), like an OS: your wallpaper is this
 * machine's, not your account's. Server-side sync (users.image) can come later
 * without changing any of these call sites.
 */
import { useSyncExternalStore, type ReactNode } from "react";

/* ---------------- wallpapers ---------------- */

export interface Wallpaper {
  id: string;
  name: string;
  /** Full CSS background value — previews and the live wall use the same string. */
  css: string;
  /** Light walls need dark lock-screen text and window shadows toned down. */
  tone: "dark" | "light";
}

export const WALLPAPERS: Wallpaper[] = [
  {
    id: "kigali",
    name: "Kigali",
    tone: "dark",
    // The Kigali Convention Centre artwork (public/wallpapers), with a soft
    // dark grade so menu-bar and lock-screen text stay legible over it.
    css: "linear-gradient(180deg, rgba(5,10,25,0.18) 0%, rgba(5,10,25,0.45) 100%), url('/wallpapers/kigali-kcc.jpg') center / cover no-repeat #08132f",
  },
  {
    id: "graphite",
    name: "Graphite",
    tone: "dark",
    css: "radial-gradient(120% 90% at 50% -10%, #3c4250 0%, transparent 55%), radial-gradient(140% 120% at 50% 40%, #23272e 0%, transparent 75%), linear-gradient(180deg, #16181c 0%, #101216 60%, #0b0c0e 100%)",
  },
  {
    id: "dusk",
    name: "Dusk",
    tone: "dark",
    css: "radial-gradient(110% 80% at 80% 100%, rgba(250,73,1,.38) 0%, transparent 60%), radial-gradient(120% 90% at 15% 0%, #2c3040 0%, transparent 60%), linear-gradient(180deg, #191b22 0%, #14151c 55%, #1a0f0c 100%)",
  },
  {
    id: "deepsea",
    name: "Deep sea",
    tone: "dark",
    css: "radial-gradient(120% 90% at 50% -10%, #274460 0%, transparent 60%), radial-gradient(120% 110% at 85% 90%, #123a3a 0%, transparent 65%), linear-gradient(180deg, #101a24 0%, #0d141c 60%, #0a0e13 100%)",
  },
  {
    id: "daylight",
    name: "Daylight",
    tone: "light",
    css: "radial-gradient(120% 90% at 50% -10%, #ffffff 0%, transparent 60%), radial-gradient(130% 100% at 80% 100%, #ffd9c4 0%, transparent 60%), linear-gradient(180deg, #f4f1ec 0%, #ece7e0 60%, #e2dbd2 100%)",
  },
  {
    id: "sky",
    name: "Sky",
    tone: "light",
    css: "radial-gradient(120% 90% at 50% -10%, #ffffff 0%, transparent 55%), radial-gradient(140% 110% at 20% 100%, #bcd9f0 0%, transparent 65%), linear-gradient(180deg, #e8f1f8 0%, #d8e7f3 60%, #c7dbec 100%)",
  },
  {
    id: "meadow",
    name: "Meadow",
    tone: "light",
    css: "radial-gradient(120% 90% at 50% -10%, #ffffff 0%, transparent 55%), radial-gradient(130% 110% at 85% 95%, #cde8cf 0%, transparent 65%), linear-gradient(180deg, #eef4ea 0%, #e2ecdc 60%, #d3e2cc 100%)",
  },
];

/* ---------------- avatars ----------------
   Head-and-shoulders people, flat colour, women and men, varied skin and hair.
   Pure inline SVG: crisp at any size, zero downloads. */

interface AvatarSpec {
  id: string;
  name: string;
  /** skin, hair, shirt, background */
  skin: string;
  hair: string;
  shirt: string;
  bg: string;
  style: "long" | "bun" | "braids" | "curly" | "short" | "afro" | "fade" | "beard";
}

const AVATAR_SPECS: AvatarSpec[] = [
  { id: "w-long", name: "Long hair", skin: "#8d5a3b", hair: "#241a12", shirt: "#e05d3d", bg: "#fde3d7", style: "long" },
  { id: "w-bun", name: "Bun", skin: "#c68863", hair: "#3a2417", shirt: "#3f7d6f", bg: "#dcefe9", style: "bun" },
  { id: "w-braids", name: "Braids", skin: "#6f4327", hair: "#171310", shirt: "#7a5fb3", bg: "#e8e1f6", style: "braids" },
  { id: "w-curly", name: "Curly", skin: "#a06a45", hair: "#4a2c17", shirt: "#c9a03a", bg: "#f7ecd2", style: "curly" },
  { id: "m-short", name: "Short hair", skin: "#8d5a3b", hair: "#1d1712", shirt: "#33688f", bg: "#dce9f3", style: "short" },
  { id: "m-afro", name: "Afro", skin: "#6f4327", hair: "#161210", shirt: "#b6552f", bg: "#f8e2d4", style: "afro" },
  { id: "m-fade", name: "Fade", skin: "#c68863", hair: "#2c2019", shirt: "#4c7a4c", bg: "#e2efdd", style: "fade" },
  { id: "m-beard", name: "Beard", skin: "#a06a45", hair: "#241a12", shirt: "#585f8f", bg: "#e3e5f4", style: "beard" },
];

function hairFor(s: AvatarSpec): ReactNode {
  switch (s.style) {
    case "long": // hair falling past the shoulders
      return (
        <>
          <path d={"M14 30c0-11 8-19 18-19s18 8 18 19v14c0 3-2 5-5 5l1-14-2-9-24 0-2 9 1 14c-3 0-5-2-5-5z"} fill={s.hair} />
          <path d="M14 40h8v12h-8zM42 40h8v12h-8z" fill={s.hair} />
        </>
      );
    case "bun":
      return (
        <>
          <circle cx="32" cy="10" r="6" fill={s.hair} />
          <path d="M15 31c0-10 7.6-18 17-18s17 8 17 18l-4-1-2-8H21l-2 8z" fill={s.hair} />
        </>
      );
    case "braids":
      return (
        <>
          <path d="M15 30c0-10 7.6-17 17-17s17 7 17 17l-3.5-.5-1.5-8H20l-1.5 8z" fill={s.hair} />
          <path d="M16 30h4v16c0 2-4 2-4 0zM24 26h4v22c0 2-4 2-4 0zM36 26h4v22c0 2-4 2-4 0zM44 30h4v16c0 2-4 2-4 0z" fill={s.hair} />
        </>
      );
    case "curly":
      return (
        <>
          <circle cx="20" cy="20" r="7" fill={s.hair} />
          <circle cx="30" cy="14" r="7.5" fill={s.hair} />
          <circle cx="41" cy="18" r="7" fill={s.hair} />
          <circle cx="46" cy="27" r="5.5" fill={s.hair} />
          <circle cx="17" cy="28" r="5.5" fill={s.hair} />
        </>
      );
    case "short":
      return <path d="M16 30c0-10 7-17 16-17s16 7 16 17l-4 0-2-7H22l-2 7z" fill={s.hair} />;
    case "afro":
      return <circle cx="32" cy="22" r="15.5" fill={s.hair} />;
    case "fade":
      return <path d="M18 28c1-9 6.5-14 14-14s13 5 14 14l-5-1-2-5H25l-2 5z" fill={s.hair} />;
    case "beard":
      return (
        <>
          <path d="M17 29c0-9 6.5-15 15-15s15 6 15 15l-4.5-.5-2-6H23.5l-2 6z" fill={s.hair} />
          <path d="M23 36v4c0 5 4 8 9 8s9-3 9-8v-4c-2 4-5 5-9 5s-7-1-9-5z" fill={s.hair} opacity="0.9" />
        </>
      );
  }
}

/** One avatar, head and shoulders only, on its soft colour disc. */
export function AvatarArt({ id, size = 40 }: { id: string; size?: number }) {
  const s = AVATAR_SPECS.find((a) => a.id === id) ?? AVATAR_SPECS[0]!;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" role="img" aria-label={s.name}>
      <defs>
        <clipPath id={`av-${s.id}`}>
          <circle cx="32" cy="32" r="31" />
        </clipPath>
      </defs>
      <circle cx="32" cy="32" r="31" fill={s.bg} />
      <g clipPath={`url(#av-${s.id})`}>
        {/* shoulders */}
        <path d="M10 64c2-12 10-18 22-18s20 6 22 18z" fill={s.shirt} />
        {/* neck */}
        <rect x="27" y="36" width="10" height="10" rx="4" fill={s.skin} />
        {/* head */}
        <ellipse cx="32" cy="28" rx="13" ry="14" fill={s.skin} />
        {/* hair */}
        {hairFor(s)}
        {/* face: simple, warm */}
        <circle cx="27" cy="28" r="1.6" fill="#20242b" />
        <circle cx="37" cy="28" r="1.6" fill="#20242b" />
        <path d="M28 34c1.3 1.5 6.7 1.5 8 0" stroke="#20242b" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      </g>
    </svg>
  );
}

export const AVATARS = AVATAR_SPECS.map((a) => ({ id: a.id, name: a.name }));

/* ---------------- the store ---------------- */

const WALL_KEY = "radar.wallpaper";
const AVATAR_KEY = "radar.avatar";

interface Personalize {
  wallpaper: string;
  avatar: string | null;
}

// Kigali (the Convention Centre artwork) is the default face of the desktop;
// a saved choice in localStorage still wins.
let state: Personalize = { wallpaper: "kigali", avatar: null };
const listeners = new Set<() => void>();

function emit() {
  for (const l of [...listeners]) l();
}

function applyWallpaper() {
  const w = WALLPAPERS.find((x) => x.id === state.wallpaper) ?? WALLPAPERS[0]!;
  const root = document.documentElement;
  root.style.setProperty("--wall-bg", w.css);
  root.dataset.wallTone = w.tone;
}

/** Load persisted choices and apply the wallpaper. Call once at boot. */
export function initPersonalize(): void {
  try {
    const w = localStorage.getItem(WALL_KEY);
    if (w && WALLPAPERS.some((x) => x.id === w)) state = { ...state, wallpaper: w };
    const a = localStorage.getItem(AVATAR_KEY);
    if (a && AVATAR_SPECS.some((x) => x.id === a)) state = { ...state, avatar: a };
  } catch {
    /* private mode — defaults stand */
  }
  applyWallpaper();
}

export function setWallpaper(id: string): void {
  if (!WALLPAPERS.some((x) => x.id === id)) return;
  state = { ...state, wallpaper: id };
  try {
    localStorage.setItem(WALL_KEY, id);
  } catch {
    /* ignore */
  }
  applyWallpaper();
  emit();
}

export function setAvatar(id: string | null): void {
  state = { ...state, avatar: id };
  try {
    if (id) localStorage.setItem(AVATAR_KEY, id);
    else localStorage.removeItem(AVATAR_KEY);
  } catch {
    /* ignore */
  }
  emit();
}

export function usePersonalize(): Personalize {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state,
  );
}
